import React, { useState, useEffect, useRef } from "react";
import { componentMapper } from "../../utils/componentMapper";
import useSidebarState from "../hooks/useSidebarState";
import { useAuth } from "../../contexts/AuthContext";
import { conditionalRender } from "../../utils/conditionalRender";
import Modal from "../modal/Modal";
import ModalContent from "../modal/ModalContent";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import fetchDataAndBuildJson from "../../utils/fetchDataAndBuildJson";
import { Navigate, useLocation } from "react-router-dom";
import AudioPlayer from "../custom_components/AudioPlayer";
import { useLocalState } from "../../contexts/LocalStateProvider";
import { getCookie } from "../../utils/cookieUtils";
import Toast from "../generic_components/Toast";
import ProgressInfoToast from "../custom_components/ProgressInfoToast";
import { useNavigate } from "react-router-dom";
import apiService from "../../api/apiService";
import CookieConsent from "../custom_components/CookieConsent";
import { usePostHog } from "posthog-js/react";
import { useGameContext } from "../../contexts/GameContext";
import axios from "axios";
import { useNotification } from "../../contexts/NotificationContext";
import FeedbackForm from "../custom_components/FeedbackForm";
import * as Sentry from "@sentry/react"

const Page = ({ jsonData, taskName, jsObjectPath }) => {
  const posthog = usePostHog();
  const [isShortSidebar, setIsShortSidebar] = useSidebarState(false);
  const { isLoggedIn, logout } = useAuth();
  const [loginStatus, setLoginStatus] = useState(isLoggedIn);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const { audioSrc, stopAudio } = useAudioPlayer();
  const [dynamicJsonData, setDynamicJsonData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const projectId = location.search.slice(11);
  const { localState, updateLocalState, clearEntireState } = useLocalState();
  const [infoMessage, setInfoMessage] = useState(null);
  const navigate = useNavigate();
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [invitationToken, setInvitationToken] = useState("");
  const [teamName, setTeamName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentStep, setCurrentStep] = useState("verifyToken"); // Steps: verifyToken, acceptInvitation
  const [generationStatus, setGenerationStatus] = useState(null);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const pollIntervalRef = useRef(null);

  // const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [isLongSidebar, setIsLongSidebar] = useState(!isShortSidebar);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const {setGamificationData} = useGameContext()
  const { setNotifications, setUnreadCount } = useNotification();

  // Create a ref for the feedback form
  const feedbackFormRef = useRef(null);

  // Add effect to ensure long sidebar on sharedTrack route
  useEffect(() => {
    if (location.pathname === "/sharedTrack") {
      setIsShortSidebar(false); // Force long sidebar for shared track page
      updateLocalState("longSidebar", true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isInitialRender) {
      setIsLongSidebar(localState.longSidebar);
    } else {
      setIsInitialRender(false);
    }
  }, [localState.longSidebar]);

  const getPartitionedKey = (key) => {
    if (window.Office && Office.context && Office.context.partitionKey) {
      return `${Office.context.partitionKey}_${key}`;
    }
    return key;
  };
  
  const getPartitionedLocalStorage = (key) => {
    return localStorage.getItem(getPartitionedKey(key));
  };
  

  // Fetch the User email
  useEffect(() => {
    const fetchAndIdentifyUser = async () => {
      const userId = getCookie("user_id") || getPartitionedLocalStorage("user_id"); // Retrieve user_id from cookies

      if (userId) {
        try {
          // Fetch the user's email using the API service
          const response = await apiService.handleProtectedRoute(
            "fetchUserEmail",
            {
              user_id: userId,
            }
          );

          if (response.success && response.email) {
            // Identify the user with PostHog
            posthog.identify(userId, {
              email: response.email,
              isLoggedIn: true,
              isAdmin: localState?.isAdmin ? true : false,
            });
            // posthog.capture('test event for logged in', {
            //   is_user_logged_in: true
            // });
            posthog.capture("Logged in user", {
              // email: response.email,
              isLoggedIn: true,
              // isAdmin: localState?.isAdmin ? true : false,
              is_user_logged_in: true
            });
            //console.log("User identified with PostHog:", response.email);
          } else if (!response.success) {
            logout();
            navigate("/");
          }
        } catch (error) {
          Sentry.captureException(error);
          logout();
          navigate("/");
          console.error("Error fetching user email:", error);
        }
      } else {
        // Allow logged-out users on specific public paths including /share/
        if (
          location.pathname !== "/accept-invitation" &&
          location.pathname !== "/discover" &&
          !location.pathname.startsWith("/share/") // Add check for /share/ paths
        ) {
          logout();
          navigate("/");
        }
      }
    };

    fetchAndIdentifyUser();
  }, [isLoggedIn, location.pathname, logout, navigate, posthog]); // Add relevant dependencies

  useEffect(() => {
    // Capture pageview with the current URL
    posthog.capture("$pageview", {
      path: location.pathname,
      url: window.location.href,
    });
  }, [location.pathname]);

  useEffect(() => {
    if (
      localState.currentRequestId &&
      !localState.generatedRequestAudio &&
      localState.projectId &&
      location.pathname !== "/chatPage"
    ) {
      const timeoutId = setTimeout(() => {
        //console.log("Timeout triggered");

        clearInterval(pollIntervalRef.current);
        // setGenerationStatus({
        //   type: "error",
        //   message: "Something went wrong with your track :(",
        //   action: {
        //     label: "Go to project",
        //     onClick: () => {
        //       navigate(`/chatPage?projectId=${localState.projectId}`);
        //     },
        //   },
        // });
        updateLocalState("currentRequestId", null);
        updateLocalState("ai_response_id", null);
      }, 20000); // 10 second timeout

      pollIntervalRef.current = setInterval(async () => {
        try {
          for (let trackId = 0; trackId < 3; trackId++) {
            const response = await fetch(
              `${process.env.REACT_APP_SERVER_ENDPOINT}/api/check-generation-status/${localState.currentRequestId}/${trackId}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`,
                },
              }
            );
      
            const data = await response.json();
            //console.log("DATA FROM CHECK-GENERATION", data)
      
            if (data.status === "ready") {
              clearTimeout(timeoutId);
              clearInterval(pollIntervalRef.current);
              updateLocalState("currentRequestId", null);
              updateLocalState("ai_response_id", null);
              // setGenerationStatus({
              //   type: "success",
              //   message: "Your track is ready to play. Check it out now!",
              //   action: {
              //     label: "Play track",
              //     onClick: () => {
              //       navigate(`/chatPage?projectId=${localState.projectId}`);
              //     },
              //   },
              // });
              break; // stop checking other tracks once one is ready
            } else if (data.status === "pending") {
              // setGenerationStatus((prev) => {
              //   if (!prev || prev.type !== "info") {
              //     return {
              //       type: "info",
              //       message:
              //         "Your track is in the works. We'll notify you when it's ready!",
              //       loading: true,
              //     };
              //   }
              //   return prev;
              // });
            }
          }
        } catch (error) {
          console.error("Error checking generation status:", error);
          Sentry.captureException(error);
        }
      }, 2000);
      

      return () => {
        clearTimeout(timeoutId);
        clearInterval(pollIntervalRef.current);
      };
    }

    return () => {
      clearInterval(pollIntervalRef.current);
    };
  }, [localState.currentRequestId, localState.projectId]);

  const handleToastClose = () => {
    clearInterval(pollIntervalRef.current); // Clear polling interval
    updateLocalState("currentRequestId", null); // Reset localState variables
    updateLocalState("ai_response_id", null); // Reset localState variables
    setGenerationStatus(null); // Clear toast state
  };

  useEffect(() => {
    let isMounted = true;
    //console.log(`Page effect triggered for path: ${location.pathname}`); // Log path

    const fetchAndBuildJson = async () => {
      if (taskName && jsObjectPath) {
        //console.log(`Page: Fetching dynamic data for task: ${taskName}`); // Log dynamic fetch
        setIsLoading(true);
        try {
          const data = await fetchDataAndBuildJson(taskName, jsObjectPath, {
            projectId,
          });
          if (isMounted) {
            //console.log("Page: Dynamic data fetched, setting state."); // Log dynamic success
            setDynamicJsonData(data);
          }
        } catch (error) {
          console.error("Error fetching and building JSON:", error);
          Sentry.captureException(error);
        } finally {
          if (isMounted) {
            //console.log("Page: Dynamic fetch finished, setting isLoading=false."); // Log dynamic finish
            setIsLoading(false);
          }
        }
      } else {
        //console.log("Page: Using provided jsonData. Type:", typeof jsonData, "Is Array:", Array.isArray(jsonData)); // Log static data type
        setDynamicJsonData(jsonData);
        //console.log("Page: Using provided jsonData. Set dynamicJsonData. isLoading=false."); // Log static finish
        setIsLoading(false);
      }
    };

    fetchAndBuildJson();

    return () => {
      //console.log(`Page effect cleanup for path: ${location.pathname}`); // Log cleanup
      isMounted = false; 
    };
  }, [taskName, jsObjectPath, projectId, jsonData, location.pathname]); 

  useEffect(() => {
    const fetchUserAdminStatus = async () => {
      if (isLoggedIn && !localState.isAdmin) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_ENDPOINT}/api/check-admin-status`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`,
                "Content-Type": "application/json",
              },
            }
          );
          const data = await response.json();
          setIsAdmin(data.isAdmin);
          updateLocalState("isAdmin", data.isAdmin);
        } catch (error) {
          console.error("Error checking admin status:", error);
          Sentry.captureException(error);
        }
      } else if (localState.isAdmin) {
        setIsAdmin(localState.isAdmin);
      }
    };

    fetchUserAdminStatus();
  }, [isLoggedIn, location.search]);

  useEffect(() => {
    if (localState.isTeamMember) setIsTeamMember(true);
  }, [isLoggedIn, location.search]);

  const checkTeamMembership = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/check-team-membership`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();

      //console.log("user is team member", data);

      if (data.isTeamMember) {
        setIsTeamMember(true);
        updateLocalState("isTeamMember", true);
      } else {
        setIsTeamMember(false);
      }
    } catch (error) {
      console.error("Error checking team membership:", error);
      Sentry.captureException(error);
      setIsTeamMember(false);
    }
  };

  const fetchGamificationData = async () => {
      try {
        const token = getCookie("token") || getPartitionedLocalStorage("token");
        const response = await axios.get(`${process.env.REACT_APP_SERVER_ENDPOINT}/api/fetchGamificationData`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        const data = response.data;
        //console.log("fetch gamification data", data)  
  
        if (data.success && data.tasks?.[0]) {
          const { user_info, taskdata } = data.tasks[0];

          //console.log("AI count : " , user_info?.ai_responses_count)
          setGamificationData({
            full_name: user_info?.full_name || "Guest",
            tasks: taskdata || [],
            streak: user_info?.streak || 0,
            completedDays: user_info?.completed_days || [],
            ai_response_count: user_info?.ai_response_count || 0,
            rank: user_info?.rank || 0,
            triggeredMilestones: user_info?.milestones || [],
          });
        } else {
          console.warn("No gamification data available.");
        }
      } catch (err) {
        console.error("Error fetching gamification data:", err);
        Sentry.captureException(err);
      }
    };

  // fetch notifications
  const getNotifications = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_SERVER_ENDPOINT}/api/userNotifications`, {
        headers: {
          Authorization: `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`,
          "Content-Type": "application/json",
        },
      });

      const data = res.data.notifications;
      // console.log("Notifications data:", data);
      const sortedData = [...res.data.notifications].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setNotifications(sortedData);
      // console.log("SetNotification :", sortedData);

      //count unread notifications
      const unreadCount = data.filter(
        (notification) => !notification.is_read
      ).length;
      setUnreadCount(unreadCount);
      // console.log("Unread notifications:", unreadCount);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      Sentry.captureException(error);
    }
  };


    useEffect(() => {
    if (isLoggedIn) {
      checkTeamMembership();
      fetchGamificationData()
      getNotifications()
    } else {
      setIsTeamMember(false);
    }
  }, [isLoggedIn]);

  const handleAcceptInvitation = () => {
    if (isLoggedIn) {
      acceptInvitation();
    } else {
      // Store the token for later use
      localStorage.setItem("invitationToken", invitationToken);
      // Navigate to signup page
      setModalContent({
        inputJson: "signup1.json", // The modal content with the reset password form
        props: {
          showCloseButton: false,
        },
      });
      setShowModal(true);
    }
  };

  useEffect(() => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");
    if (!token) {
      clearEntireState();
      stopAudio();
    } else if (token) updateLocalState("songs", null);
  }, [isLoggedIn]);

  const acceptInvitation = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/acceptInvitation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Include the authorization header if the user is logged in
            ...(isLoggedIn && {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            }),
          },
          body: JSON.stringify({ token: invitationToken }),
        }
      );

      const result = await response.json();

      if (result.success) {
        // Invitation accepted
        // Redirect to home or team page
        navigate("/home");
      } else {
        setErrorMessage(result.message);
      }
    } catch (error) {
      console.error("Error accepting invitation:", error);
      Sentry.captureException(error);
      setErrorMessage("Failed to accept invitation.");
    }
  };

  useEffect(() => {
    if (location.pathname === "/accept-invitation") {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get("token");
      setInvitationToken(token);

      if (token) {
        // Verify token with the backend
        const verifyToken = async () => {
          try {
            const response = await fetch(
              `${process.env.REACT_APP_SERVER_ENDPOINT}/api/validateInvitationToken?token=${token}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            const result = await response.json();

            if (result.success) {
              setTeamName(result.teamName);
              setCurrentStep("acceptInvitation");
              setIsLoading(false);
            } else {
              setErrorMessage(result.message);
              setIsLoading(false);
            }
          } catch (error) {
            console.error("Error verifying invitation token:", error);
            Sentry.captureException(error);
            setErrorMessage("Failed to verify invitation.");
            setIsLoading(false);
          }
        };
        verifyToken();
      } else {
        setErrorMessage("Invalid invitation link.");
        setIsLoading(false);
      }
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    //console.log(token);

    if (token && location.pathname !== "/accept-invitation") {
      // Make the API request to verify the token
      const verifyToken = async () => {
        try {
          const response = await apiService.sendRequest("verifyResetToken", {
            token,
          });

          if (response.success) {
            // If the token is valid, set the token validation status to true
            setModalContent({
              inputJson: "resetPassword3.json", // The modal content with the reset password form
            });
            setShowModal(true);
          } else {
            // If the token is invalid, show an error message
            // console.log("Invalid or expired token");
          }
        } catch (error) {
          // Handle anyors
          // console.log(error);
          Sentry.captureException(error);
          setInfoMessage("Invalid or expired token password reset link.");
          navigate("/");
        }
      };

      verifyToken();
    } else {
      // console.log("No token found in the URL.");
    }
  }, [location.search]);

  useEffect(() => {
    if (!isLoggedIn && localState.showLoginModal) {
      setModalContent({
      inputJson: "oauthSignIn.json",
      });
      setShowModal(true);
    }
  }, [localState.showLoginModal]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // For Windows/Linux: Ctrl+I
      // For macOS: Command+I
      if ((event.ctrlKey || event.metaKey) && event.key === "i") {
        event.preventDefault(); // Prevent the default behavior if needed
        posthog.capture("Click on Create New Button through keyboard");
        if (!isLoggedIn) {
          setModalContent({
            inputJson: "signIn.json",
          });
          setShowModal(true);
        } else {
          // Set the modal content when Ctrl+I or Cmd+I is pressed
          setModalContent({
            name: "customInput",
            props: {
              className: "md:w-[40rem] w-full",
              showCloseButton: false,
              easyClose: true,
              requestData: {
                createProject: true,
              },
              chatPage: false,
            },
          });
          setShowModal(true);
        }
      }
    };

    // Attach the event listener
    window.addEventListener("keydown", handleKeyDown);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isLoggedIn]);

  // Utility function to convert a buffer to a Data URL for images
  const bufferToDataURL = (buffer, mimeType) => {
    const binary = Array.from(buffer)
      .map((b) => String.fromCharCode(b))
      .join("");
    const base64 = btoa(binary);
    return `data:${mimeType};base64,${base64}`;
  };

  const renderComponents = (data, parentKey = 'root') => {
    if (!data) {
      // console.error("Data is null or undefined"); // Keep logging minimal
      return null;
    }
    
    if (!Array.isArray(data)) {
      // console.error("Invalid jsonData format or empty array:", data); // Keep logging minimal
      
      if (taskName === "fetchChatHistory") {
        navigate("/");
      }
      return null;
    }

    return data.map((item, index) => {
      // Add a check for item itself being null or undefined for robustness
      if (!item) {
        // console.warn(`[Page.js:renderComponents] Encountered null or undefined item at index ${index} in data array. Skipping.`);
        return null; 
      }
      //console.log("Rendering item:", item.name);

      // Modified conditional rendering for shared track route
      const conditionalResult = conditionalRender(
        item,
        isShortSidebar,
        setIsShortSidebar,
        renderComponents,
        isLoggedIn, // Allow rendering for shared track even if not logged in
        location,
        isTeamMember, // Allow rendering for shared track even if not a team member
        isAdmin,
        localState
      );
      
      if (conditionalResult) {
        return <div key={`${parentKey}-cond-${index}`}>{conditionalResult}</div>;
      }

      // If conditionalRender returned falsy (e.g., null or undefined),
      // and the item itself does not have a 'name' property,
      // it means it's likely a conditional descriptor (e.g., { conditionalInviteJson: ... })
      // whose condition was false, or an otherwise unrenderable item that's not a standard component.
      // In this case, we skip rendering it directly to avoid the "Component not found in mapper: undefined" error.
      if (!item.name) {
        return null;
      }

      const { name, props, children } = item;

      // Special handling for span elements with text property
      if (name === "span" && props && props.text) { // Added check for props
        return <span key={index} className={props.className}>{props.text}</span>;
      }

      if (name === "AudioPlayerPlaceholder") {
        return audioSrc ? <AudioPlayer key={`${parentKey}-audio-${index}`} /> : null;
      }

      const ComponentToRender = componentMapper[name];

      if (!ComponentToRender) {
         // Use console.warn for non-critical issues
         console.warn(`[Page.js:renderComponents] Component not found in mapper: ${name}. Skipping item:`, item);
         return null; // Skip rendering this unknown component
      }

      let componentProps = { ...props };      // Handle sidebar toggle specifically (example of modifying onClick)
      if ((name === 'button' || name === 'div') && props?.trigger === 'toggleSidebar') {
        // Log when this component is being rendered
        //console.log(`[Page.js] Rendering component with toggleSidebar trigger:`, item.props?.id || name, item);
        // Add an onClick handler specifically for logging the click event AND toggling the state
        componentProps.onClick = () => {
          // Log PostHog event for sidebar collapse/expand
          posthog.capture("Sidebar-Toggle", {
            action: isShortSidebar ? "expand" : "collapse",
            location: "sidebar",
            buttonId: item.props?.id || "sidebar-toggle"
          });
          
          // *** Add the state toggling logic ***
          setIsShortSidebar(!isShortSidebar);
        };
      }

      // Handle the specific 'openUploadModal' action for buttons
      if (name === 'button' && props?.onClick?.action === 'openUploadModal') {
        componentProps.onClick = () => {
          const modalParams = props.onClick.params;
          posthog.capture("Click-CustomPromptButton-Homepage", {
          caption: props.caption,
          title: modalParams?.title,
          description: modalParams?.description?.[0] || "", // first line of description
        });
          console.log(`[Page.js] Opening custom prompt modal for button: ${props.caption}`, modalParams);
          const caption = props.caption;
          // Set the modal title in localState so CustomInput can use it
          updateLocalState('currentModalTitleForPrompt', modalParams.title);
          setModalContent({
            name: "CustomPromptModal",
            props: {
              title: modalParams.title,
              description: modalParams.description,
              fileTypes: modalParams.fileTypes,
              easyClose: true,
              handleFileChange: handleModalFileChange,
              setToastMessage: setInfoMessage
            }
          });
          setShowModal(true);
        };
      }

      // Handle OTHER existing modal triggers (ensure no conflict)
      // Example: Generic toggleModal trigger on a div
      if (name === "div" && props.trigger === "toggleModal") {
         componentProps.onClick = () => {
              setModalContent(props.modalContent);
              setShowModal(true);
          };
      }

      // Example: PromptButton modal toggle
      if (name === "PromptButton") {
          componentProps.onModalToggle = (modalContent) => {
              setModalContent(modalContent);
              setShowModal(true);
          };
      }

      // Handle InputWithButton or button modal toggles
      if ((name === "InputWithButton" || name === "button") && props.trigger === "toggleModal") {
          // ALWAYS provide the default onClick handler if modalContent is defined
          if (props.modalContent) {
              componentProps.onClick = () => {
                 setModalContent(props.modalContent); // Default case (e.g., signup2.json)
                 setShowModal(true);
               };
          }

          // Conditionally provide Option 1 handler if modalContent1 is defined
          if (props.modalContent1) {
             componentProps.onClickOption1 = () => {
                setModalContent(props.modalContent1); // Option 1 (e.g., signIn.json)
                setShowModal(true);
              };
          }
          // Conditionally provide Option 2 handler if modalContent2 is defined
           if (props.modalContent2) {
             componentProps.onClickOption2 = () => {
                setModalContent(props.modalContent2); // Option 2 (e.g., rsvp.json)
                setShowModal(true);
              };
          }
      }

      // Handle closeModal trigger
      if (props?.trigger === "closeModal") {
          componentProps.onClick = () => {
            closeModal();
          };
      }

      // Pass closeModal function to InputWithButton if needed
      if (name === "InputWithButton") {
          componentProps.closeModal = () => setShowModal(false);
      }

      const currentKey = `${parentKey}-comp-${index}`;

      return (
        <>
          <ComponentToRender key={currentKey} {...componentProps}>
            {children && renderComponents(children, currentKey)}
          </ComponentToRender>
        </>
      );
    });
  };

  const handleModalFileChange = async (event) => {
     const selectedFile = event.target.files ? event.target.files[0] : null;
     if (!selectedFile) {
       console.log("No file selected from modal.");
       setInfoMessage({type: 'error', message: 'No file selected.'})
       return;
     }

     // Basic validation (can reuse from CustomInput if needed)
     const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
     if (!isFileTypeAllowed(selectedFile)) { // Assuming isFileTypeAllowed is accessible or redefined here
        posthog.capture("Invalid file type from modal", { fileName: selectedFile.name, fileType: selectedFile.type, is_user_logged_in: true });
        setInfoMessage({type: 'error', message: 'Invalid file type.'});
        return;
     }
      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        posthog.capture("File too large from modal", { fileName: selectedFile.name, fileSize: selectedFile.size, is_user_logged_in: true });
        setInfoMessage({type: 'error', message: 'File exceeds 20 MB limit.'});
        return;
      }

      // ---- Simulate Upload and Update Shared State ----
      // NOTE: Replace this section with your actual file upload logic
      // to get the final URL. Using a placeholder for now.
      console.log("Simulating upload for:", selectedFile.name);
      // Simulating async upload
      await new Promise(resolve => setTimeout(resolve, 500));
      const simulatedPublicUrl = `/uploads/simulated/${selectedFile.name}`; // Placeholder URL
      console.log("Simulated upload complete. URL:", simulatedPublicUrl);
      // ---- End Simulation ----


      const fileObjectForInput = {
        name: selectedFile.name,
        type: selectedFile.type,
        file: selectedFile, // The actual File object
        url: simulatedPublicUrl, // The URL from upload (using simulation here)
      };

      // Update local state - CustomInput will listen for this
      updateLocalState('uploadedFileFromModal', fileObjectForInput);

      setInfoMessage({type: 'success', message: `Attached: ${selectedFile.name}`});
      closeModal(); // Close the modal
  };

   // Helper function for file type check (copied/adapted from CustomInput)
   const isFileTypeAllowed = (file) => {
      const allowedMimeTypes = new Set([ /* ... Mime types ... */ 'text/plain', 'application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif', 'video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/x-flv', 'video/mpg', 'video/webm', 'audio/wav', 'audio/mpeg', 'audio/aiff', 'audio/aac', 'audio/ogg', 'audio/flac']);
      const allowedExtensions = new Set([ /* ... Extensions ... */ '.txt', '.pdf', '.doc', '.docx', '.rtf', '.dot', '.dotx', '.hwp', '.hwpx', '.png', '.jpeg', '.jpg', '.webp', '.heic', '.heif', '.mp4', '.mpeg', '.mov', '.avi', '.flv', '.mpg', '.webm', '.wav', '.mp3', '.aiff', '.aac', '.ogg', '.flac']);
      if (!file) return false;
      const nameParts = file.name.split('.');
      const fileExtension = nameParts.length > 1 ? '.' + nameParts.pop().toLowerCase() : '';
      return allowedMimeTypes.has(file.type?.toLowerCase()) || allowedExtensions.has(fileExtension);
   };

  const closeModal = () => {
    setShowModal(false);
    setModalContent(null);
    updateLocalState({ showLoginModal: false });
  };  // Add new click outside handler for all modals including Feedback
  useEffect(() => {
    const handleOutsideClick = (event) => {
      // Special handling for feedback form
      const isFeedbackForm = modalContent && 
                            (modalContent.name === "FeedbackForm" || 
                             (modalContent.props && modalContent.props.feedbackForm === true));
      
      if (showModal) {
        // For feedback form, check if the click is outside the form element
        if (isFeedbackForm && feedbackFormRef.current && 
            !feedbackFormRef.current.contains(event.target)) {
          closeModal();
          return;
        }
        
        // For ALL modals, check if click is on the overlay
        // This enables close on click outside for all modals including sign-in and sign-up
        if (event.target.classList.contains('modal-overlay')) {
          closeModal();
        }
      }
    };

    if (showModal) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showModal, modalContent]);

  // Add ESC key handler for all modals
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showModal) {
        closeModal();
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showModal]);

  return (
    <div>
      {isLoading ? (
        taskName === "fetchChatHistory" ? (
          <>
            {(() => {
              const jsonFile = require(`../../jsonFiles/chatPage.json`);
              return renderComponents(jsonFile);
            })()}
          </>
        ) : location.pathname === "/settings" ? (
          (() => {
            const jsonFile = require(`../../jsonFiles/settings.json`);
            return renderComponents(jsonFile);
          })()
        ) : (
          (() => {
            const jsonFile = require(`../../jsonFiles/loaderPage.json`);
            return renderComponents(jsonFile);
          })()
        )
      ) : (
        (() => {
          const dataToRender = dynamicJsonData || jsonData;
          // console.log(
          //   `Page Render: Path=${location.pathname}, Rendering data?`, 
          //   !!dataToRender, 
          //   `Type=${typeof dataToRender}`, 
          //   `IsArray=${Array.isArray(dataToRender)}`,
          //   // Log the first item's name and props if it's an array
          //   (Array.isArray(dataToRender) && dataToRender.length > 0) ? `FirstItem=${JSON.stringify({ name: dataToRender[0].name, props: dataToRender[0].props })}` : '(No array or empty)'
          // );
          return dataToRender && renderComponents(dataToRender);
        })()
      )}
      {/* Comment out the cookie consent component to prevent it from showing after signup */}
      {/* {showCookieConsent && <CookieConsent onAccept={handleAcceptCookies} />} */}      {/* Modal Rendering with click outside support */}
      {showModal && (
        <Modal
          closeModal={closeModal}
          showCloseButton={modalContent?.props?.showCloseButton !== false} 
          easyClose={true} /* Always enable click outside to close for all modals */
          className="modal-overlay"
        >
          {modalContent &&
            renderComponents([
              { 
                ...modalContent, 
                props: { 
                  ...modalContent.props, 
                  closeModal,
                  // Pass the ref when it's a feedback form
                  ...(modalContent.name === "FeedbackForm" || 
                     (modalContent.props && modalContent.props.feedbackForm === true) 
                     ? { ref: feedbackFormRef } : {})
                }
              },
            ])}
        </Modal>
      )}
      {location.pathname == "/" && localState.lowCreditsWarning && (
        <ProgressInfoToast
          type={localState.lowCreditsWarning.type}
          message={localState.lowCreditsWarning.message}
          action={localState.lowCreditsWarning.action}
          onClose={() => updateLocalState("lowCreditsWarning", null)}
          excludeCloseButton={true}
          className={`md:text-nowrap ${!isLongSidebar
            ? `md:top-4 top-20 md:left-[84px] md:max-w-[calc(100vw-100px)] w-[90%] md:translate-x-0 -translate-x-1/2 left-[50%]`
            : `md:top-4 top-20 md:left-[228px] md:max-w-[calc(100vw-244px)] w-[90%] md:translate-x-0 -translate-x-1/2 left-[50%]`
            } `}
        />
      )}
      {/* {!showModal && location.pathname == "/" && !isLoggedIn && (
        <ProgressInfoToast
          type={"info"}
          message={"🎸 Beta unlocked! AI leveled up! Sign up! 🎉"}
          excludeCloseButton={true}
          className={`md:w-fit w-[90%] md:text-nowrap md:top-4 top-20 md:left-[57%] -translate-x-1/2 left-[50%] ${!isLongSidebar
            ? `md:top-4 top-20 md:left-[52.5%] -translate-x-1/2 left-[50%]`
            : `md:top-4 top-20 md:left-[57%] -translate-x-1/2 left-[50%]`
            } `}
        />
      )} */}
      {generationStatus && (
        <ProgressInfoToast
          type={generationStatus?.type}
          message={generationStatus?.message}
          action={generationStatus?.action}
          loading={generationStatus?.loading}
          onClose={handleToastClose}
          className={`md:w-fit w-[90%] md:text-nowrap ${!isLongSidebar
            ? `md:top-4 top-20 md:left-[52.5%] -translate-x-1/2 left-[50%]`
            : `md:top-4 top-20 md:left-[57.5%] -translate-x-1/2 left-[50%]`
            } `}
        />
      )}
      {infoMessage && (
        <Toast
           type={infoMessage.type || 'info'}
           message={infoMessage.message}
           onClose={() => setInfoMessage(null)}
         />
       )}
    </div>
  );
};

export default Page;